/**
 * 记忆。两类共存，各自的边界很重要：
 *
 *   ConversationMemory  单次对话的摘要，每个会话一条
 *   MemoryIndex         唯一持有向量的表，是可重建的索引而非真值
 */

import { Input, Select, Space, Tabs, Tooltip, Typography } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { DataTable, useTableParams } from "../../components/DataTable";
import { useCharacterScope } from "../../services/character";
import { fmtNumber, RelativeTime, StatusTag, Tags } from "../../components/format";

export function MemoriesPage() {
  const [tab, setTab] = useState("conversation");
  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: "conversation", label: "会话摘要", children: <ConversationMemories /> },
        { key: "index", label: "向量索引", children: <MemoryIndexTable /> },
      ]}
    />
  );
}

const CM_FILTERS = ["search", "sort", "order"];

function ConversationMemories() {
  const { searchParams, setParam } = useTableParams(CM_FILTERS);
  const scope = useCharacterScope();
  return (
    <DataTable
      path="/api/v1/admin/memories/conversation"
      fixedParams={scope}
      filterKeys={CM_FILTERS}
      toolbar={
        <Space wrap>
          <Input.Search
            allowClear
            style={{ width: 300 }}
            placeholder="标题 / 摘要 / 标签"
            defaultValue={searchParams.get("search") ?? ""}
            onSearch={(v) => setParam("search", v)}
          />
          <Select
            style={{ width: 180 }}
            value={searchParams.get("sort") ?? "startedAt"}
            onChange={(v) => setParam("sort", v)}
            options={[
              { value: "startedAt", label: "按开始时间" },
              { value: "characterAffinityDelta", label: "按好感增量" },
              { value: "messageCount", label: "按消息数" },
              { value: "tokenCount", label: "按 token" },
            ]}
          />
        </Space>
      }
      columns={[
        { title: "标题", dataIndex: "title", width: 200, render: (v) => v || "—" },
        { title: "摘要", dataIndex: "summary", render: (v) => v || "—" },
        { title: "标签", dataIndex: "tags", width: 180, render: (v: string[]) => <Tags values={v} /> },
        { title: "梦境标签", dataIndex: "dreamTags", width: 150, render: (v: string[]) => <Tags values={v} /> },
        {
          title: "序号范围",
          dataIndex: "sequenceRange",
          width: 110,
          render: (v: number[]) => `#${v?.[0]} – #${v?.[1]}`,
        },
        { title: "消息", dataIndex: "messageCount", width: 70, align: "right" },
        { title: "token", dataIndex: "tokenCount", width: 90, align: "right", render: fmtNumber },
        { title: "好感快照", dataIndex: "characterAffinitySnapshot", width: 90, align: "right" },
        {
          title: "好感增量",
          dataIndex: "characterAffinityDelta",
          width: 90,
          align: "right",
          // 新行是「该区间内逐条增量之和」，可以超出 ±10。
          // 迁移来的行（fromMessageSequence = toMessageSequence = 0）是旧系统的
          // 模型综合评价，语义不同，标记出来免得被当成同一个口径比较。
          render: (v: number, r) => {
            const range = (r as { sequenceRange?: number[] }).sequenceRange;
            const legacy = range?.[0] === 0 && range?.[1] === 0;
            return (
              <Tooltip title={legacy ? "旧系统的模型综合评价，非逐条求和" : "该区间内逐条增量之和"}>
                <span style={{ color: v > 0 ? "#2F7A4B" : v < 0 ? "#B4232E" : undefined }}>
                  {v > 0 ? `+${v}` : v}
                  {legacy ? <span style={{ color: "#999" }}> *</span> : null}
                </span>
              </Tooltip>
            );
          },
        },
        {
          title: "用户",
          dataIndex: "userName",
          width: 120,
          render: (v: string | null, r: { userId: string }) => <Link to={`/users/${r.userId}`}>{v || "—"}</Link>,
        },
        {
          title: "会话",
          dataIndex: "conversationId",
          width: 100,
          render: (v: string) => <Link to={`/conversations/${v}`}>{v.slice(-8)}</Link>,
        },
        { title: "开始", dataIndex: "startedAt", width: 130, render: (v) => <RelativeTime value={v} /> },
      ]}
    />
  );
}

const INDEX_FILTERS = ["search", "sourceType", "embeddingStatus"];

function MemoryIndexTable() {
  const { searchParams, setParam } = useTableParams(INDEX_FILTERS);
  return (
    <>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        唯一持有向量的表（<Typography.Text code>vector(1024)</Typography.Text>，pgvector HNSW 索引上限 2000 维）。
        它是**可重建的索引**而不是真值——PostgreSQL 里的业务表才是。
      </Typography.Paragraph>
      <DataTable
        path="/api/v1/admin/memories/index"
        filterKeys={INDEX_FILTERS}
        toolbar={
          <Space wrap>
            <Input.Search
              allowClear
              style={{ width: 280 }}
              placeholder="内容"
              defaultValue={searchParams.get("search") ?? ""}
              onSearch={(v) => setParam("search", v)}
            />
            <Select
              allowClear
              style={{ width: 220 }}
              placeholder="来源类型"
              value={searchParams.get("sourceType") ?? undefined}
              onChange={(v) => setParam("sourceType", v)}
              options={[
                "conversation_memory",
                "relationship_diary",
                "character_diary",
                "knowledge_chunk",
              ].map((v) => ({ value: v, label: v }))}
            />
            <Select
              allowClear
              style={{ width: 160 }}
              placeholder="向量状态"
              value={searchParams.get("embeddingStatus") ?? undefined}
              onChange={(v) => setParam("embeddingStatus", v)}
              options={["pending", "ready", "stale"].map((v) => ({ value: v, label: v }))}
            />
          </Space>
        }
        columns={[
          { title: "来源", dataIndex: "sourceType", width: 190 },
          { title: "范围", dataIndex: "scope", width: 130 },
          { title: "标题", dataIndex: "title", width: 180, render: (v) => v || "—" },
          { title: "内容", dataIndex: "content" },
          { title: "召回次数", dataIndex: "recallCount", width: 90, align: "right" },
          { title: "模型", dataIndex: "embeddingModel", width: 170 },
          { title: "维度", dataIndex: "embeddingDimensions", width: 70, align: "right" },
          { title: "向量状态", dataIndex: "embeddingStatus", width: 100, render: (v) => <StatusTag value={v} /> },
          { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
        ]}
      />
    </>
  );
}
